/**
 * Taxonomy (Offerings `dimensions`) slug→label resolver.
 *
 * The DCS triad (degree / category / subject) travels through the app as
 * canonical *slugs* (`masters`, `business-intelligence`) so branching, the
 * cascading `dimensions` query, and the Offerings `/match` filter all agree.
 * But display copy — question placeholders, the selected chip, `_label`
 * interpolation — needs the human *label* ("Master's", "Business
 * Intelligence").
 *
 * When the user selects in-flow (or the DSL entry step is rendered), the
 * DataSourceManager / bound Select supplies the label. The gap is a route
 * that has no bound DCS Select, such as a route that jumps past the entry step
 * or an Editorial Listing-only entry. For those cases this module exposes
 * `fetchTaxonomySeed`; callers resolve it before mounting `TaxonomyProvider`.
 *
 * Caching: `fetchTaxonomySeed` is meant to run in an RSC with Next's
 * `{ next: { revalidate } }`, and the Offerings response is itself
 * `Cache-Control: public, max-age=21600` + CDN. Failures soft-fail to an
 * empty seed (callers then fall back to the slug — never worse than today).
 *
 * Transport: the `dimensions` call goes through the shared
 * `OfferingsServiceApiClient` (same base/contract as the other
 * `/v1/offerings/*` calls), not a bespoke `fetch`.
 */
import {
  OfferingsServiceApiClient,
  type APIRequestOptions,
  type DimensionsResponse,
} from '@highereducation/offerings';

export type { DimensionsResponse };

/** DCS field key -> Offerings taxonomy namespace. */
export const DCS_NAMESPACE = {
  degree: 'degree_level',
  category: 'academic_subject_category',
  subject: 'academic_subject',
} as const;

export type DcsKey = keyof typeof DCS_NAMESPACE;

/** Own-property check so prototype keys like `toString` are not DCS fields. */
export function isDcsKey(value: string): value is DcsKey {
  return Object.prototype.hasOwnProperty.call(DCS_NAMESPACE, value);
}

/** The taxonomy namespaces backing the DCS triad. */
export const DCS_NAMESPACES: readonly string[] = Object.values(DCS_NAMESPACE);

/**
 * How much of the taxonomy to keep when building a seed. The `dimensions`
 * endpoint always returns the full catalog (it can't be filtered server-side),
 * so this controls what we *keep* and ship to the client:
 *  - `'dcs'`   — only the DCS triad namespaces (what `/discover` needs today).
 *  - `'all'`   — every namespace (for consumers that translate arbitrary slugs).
 *  - `string[]`— an explicit namespace allowlist.
 */
export type TaxonomyScope = 'dcs' | 'all' | readonly string[];

/** Resolve a scope to a namespace allowlist (`undefined` = keep everything). */
function scopeToNamespaces(
  scope: TaxonomyScope,
): ReadonlySet<string> | undefined {
  if (scope === 'all') return undefined;
  if (scope === 'dcs') return new Set(DCS_NAMESPACES);
  return new Set(scope);
}

/** Map key is `${namespace}:${slug}`; value is the display label. */
export type TaxonomyMap = Map<string, string>;

/**
 * Serializable form of {@link TaxonomyMap} — an array of `[key, label]`
 * entries. Used to pass an SSR-prefetched map across the RSC server→client
 * boundary (a raw `Map` survives Flight serialization, but entries are the
 * portable, framework-agnostic contract).
 */
export type TaxonomySeedEntry = [string, string];
export type TaxonomySeed = ReadonlyArray<TaxonomySeedEntry>;

/** Normalize a seed (Map or entries) into a {@link TaxonomyMap}. */
export function seedToMap(seed: TaxonomyMap | TaxonomySeed): TaxonomyMap {
  return seed instanceof Map ? seed : new Map(seed);
}

const taxonomyKey = (namespace: string, slug: string): string =>
  `${namespace}:${slug}`;

/**
 * Build the `(namespace, slug) -> label` map from a dimensions response.
 * Pass `namespaces` to keep only those namespaces (e.g. the DCS triad);
 * omit it to keep every namespace in the response.
 */
export function buildTaxonomyMap(
  response: DimensionsResponse,
  namespaces?: ReadonlySet<string>,
): TaxonomyMap {
  const map: TaxonomyMap = new Map();
  const dimensions = response?.dimensions ?? {};
  for (const [namespace, dimension] of Object.entries(dimensions)) {
    if (namespaces && !namespaces.has(namespace)) continue;
    for (const node of dimension?.nodes ?? []) {
      const slug = typeof node.key === 'string' ? node.key : undefined;
      const label = typeof node.label === 'string' ? node.label : undefined;
      if (slug && label) map.set(taxonomyKey(namespace, slug), label);
    }
  }
  return map;
}

/**
 * Resolve a slug to its label within a namespace. Returns the slug
 * unchanged on a miss so callers can use it as a safe identity fallback.
 */
export function translateTaxonomySlug(
  map: TaxonomyMap,
  slug: string,
  namespace: string,
): string {
  return map.get(taxonomyKey(namespace, slug)) ?? slug;
}

/** Strip trailing slashes so the request URL is stable across callers. */
const normalizeBase = (baseUrl: string): string => baseUrl.replace(/\/+$/, '');

/**
 * Fetch the taxonomy as a serializable seed (entries array). Server routes can
 * pass the result to `<DynamicExperience taxonomySeed=…>` for a flash-free
 * first paint; the client runtime also uses it before mounting unbound-DCS
 * entry flows. Soft-fails to `[]` (callers fall back to the slug).
 *
 * `init.scope` selects how much of the catalog to keep in the seed
 * (defaults to `'all'`); `/discover` passes `'dcs'` since it only needs the
 * triad. The rest of `init` is forwarded to the Offerings client request
 * (incl. Next's `next` cache hint) so RSC callers can set
 * `{ next: { revalidate } }` and reuse the framework data cache.
 */
export async function fetchTaxonomySeed(
  baseUrl: string,
  init?: APIRequestOptions & { scope?: TaxonomyScope },
): Promise<TaxonomySeed> {
  const { scope = 'all', ...requestOptions } = init ?? {};
  try {
    const client = new OfferingsServiceApiClient({
      baseUrl: normalizeBase(baseUrl),
    });
    const response = await client.getDimensions(requestOptions);
    if (!response) return [];
    const map = buildTaxonomyMap(response, scopeToNamespaces(scope));
    return Array.from(map.entries());
  } catch {
    return [];
  }
}
